import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cardHolderAPI,
  CreateCardHolderData,
  UpdateCardHolderData,
} from "../../services/cardHolderAPI";
import { useAuthStore } from "../../stores/authStore";
import CardHolderFormFields, {
  CardHolderFormValues,
} from "../../components/cardHolders/CardHolderFormFields";
import { normalizeAadhar } from "../../utils/aadhar";
import { tokens } from "../../theme";

const cardHolderSchema = yup.object({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup.string().email("Invalid email format").optional(),
  phone: yup.string().required("Phone is required"),
  aadharNumber: yup
    .string()
    .required("Aadhar number is required")
    .test("aadhar", "Aadhar must be 12 digits", (v) => normalizeAadhar(v || "").length === 12),
  city: yup.string().required("City is required"),
  block: yup.string().required("Block is required"),
  district: yup.string().required("District is required"),
  state: yup.string().required("State is required"),
  address: yup.string().required("Complete address is required"),
  status: yup.string().oneOf(["active", "inactive"]).required(),
});

const CardHolderFormPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [alert, setAlert] = React.useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const { data: holder, isLoading } = useQuery({
    queryKey: ["cardHolder", id],
    queryFn: () => cardHolderAPI.getCardHolderById(id!),
    enabled: isEdit && !!id,
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CardHolderFormValues>({
    resolver: yupResolver(cardHolderSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      aadharNumber: "",
      city: "",
      block: "",
      district: "",
      state: "",
      address: "",
      status: "active",
    },
  });

  React.useEffect(() => {
    if (holder) {
      reset({
        firstName: holder.holderName.split(" ")[0] || "",
        lastName: holder.holderName.split(" ").slice(1).join(" ") || "",
        email: holder.email || "",
        phone: holder.phone,
        aadharNumber:
          holder.aadharNumber && !String(holder.aadharNumber).includes("X")
            ? holder.aadharNumber
            : "",
        city: holder.city || "",
        block: holder.block || "",
        district: holder.district || "",
        state: holder.state || "",
        address: holder.address || "",
        status: holder.isActive ? "active" : "inactive",
      });
    }
  }, [holder, reset]);

  const buildPayload = (formData: CardHolderFormValues, includeAadhar = true) => {
    const aadhar = normalizeAadhar(formData.aadharNumber);
    return {
    holderName: `${formData.firstName} ${formData.lastName}`.trim(),
    email: formData.email || "",
    phone: formData.phone,
    ...(includeAadhar && aadhar.length === 12 ? { aadharNumber: aadhar } : {}),
    city: formData.city.trim(),
    block: formData.block.trim(),
    district: formData.district.trim(),
    state: formData.state.trim(),
    address: formData.address.trim(),
    ...(user?.branchId ? { branchId: user.branchId } : {}),
  };
  };

  const { mutate: createHolder, isPending: isCreating } = useMutation({
    mutationFn: (data: CreateCardHolderData) => cardHolderAPI.createCardHolder(data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["cardHolders"] });
      navigate(`/cardholders/${created.id}`);
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to create card holder",
      });
    },
  });

  const { mutate: updateHolder, isPending: isUpdating } = useMutation({
    mutationFn: (data: UpdateCardHolderData) => cardHolderAPI.updateCardHolder(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cardHolders"] });
      queryClient.invalidateQueries({ queryKey: ["cardHolder", id] });
      navigate(`/cardholders/${id}`);
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to update card holder",
      });
    },
  });

  const onSubmit = (formData: CardHolderFormValues) => {
    if (isEdit) {
      updateHolder({
        ...buildPayload(formData, false),
        isActive: formData.status === "active",
      });
    } else {
      createHolder(buildPayload(formData, true) as CreateCardHolderData);
    }
  };

  if (isEdit && isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(isEdit ? `/cardholders/${id}` : "/cardholders")}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: tokens.radius.md,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {isEdit ? "Edit card holder" : "Add card holder"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Aadhar number is required. It is shown masked on lists and profile.
        </Typography>

        {alert && (
          <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
            {alert.message}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <CardHolderFormFields
            control={control}
            errors={errors}
            watch={watch}
            showStatus={isEdit}
          />
          <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button
              color="inherit"
              onClick={() => navigate(isEdit ? `/cardholders/${id}` : "/cardholders")}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isCreating || isUpdating}>
              {isCreating || isUpdating ? "Saving…" : isEdit ? "Save changes" : "Create holder"}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default CardHolderFormPage;
